import { memo, useCallback, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import * as Cart from '@Queries/Cart';
import { useDeps } from '@Contexts/DI';
import { useMeta } from '@Queries/Meta';
import useNavigation from '@Navigation/useNavigation';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './ConstructorBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { CartModalInputProduct } from '@Types/Cart';
import type { ProductData } from '@Types/Product';

export interface BuyButtonProps extends ButtonProps {
  className?: string;
  product: ProductData;
}

interface Parameter {
  id: number;
  count: number;
  parameter_id: number;
}

const ConstructorBuyButton: FC<BuyButtonProps> = (props) => {
  const { className, product, ...restProps } = props;
  const { t } = useTranslation();
  const { openPage } = useNavigation();
  const meta = useMeta();
  const { region } = meta.data;
  const { logger } = useDeps();
  const addToCart = Cart.useAddToCart();

  const constructorParameterValues = useMemo(
    () => product.constructorParameterValues || [],
    [product.constructorParameterValues],
  );

  const parameters = useMemo<Parameter[]>(() => {
    return constructorParameterValues.map((p) => ({
      id: p.value,
      count: p.count,
      parameter_id: p.parameterIds[0],
    }));
  }, [constructorParameterValues]);

  // Добавляем значения выбранных опций
  const compiledProduct = useMemo<CartModalInputProduct>(() => {
    return {
      image: product.images[0]?.src,
      shopProductId: product.id,
      parameterValues: parameters,
      quantity: 1,
      isModifiedParameters: true,
      fabricValues: constructorParameterValues
        .filter((val) => val.type === 'fabric')
        .map((p) => ({
          fabric_id: p.value,
          parameter_id: p.parameterIds[0],
        })),
    };
  }, [parameters, product.id, product.images, constructorParameterValues]);

  const hasInCart = Cart.useHasInCart(compiledProduct);

  const handleError = useCallback(
    (err: unknown) => {
      logger.log(err);
    },
    [logger],
  );

  const handleClick = useCallback(() => {
    if (hasInCart) {
      openPage({ url: `${region.url}/order/check` });
      return;
    }

    if (addToCart.isPending || addToCart.isError) {
      return;
    }

    addToCart.mutate(
      {
        products: [
          {
            ...compiledProduct,
            isModular: false,
            paymentTypeId: -1,
          },
        ],
        options: { isRelated: false },
        analyticsData: {
          products: [product],
          label: 'Избранное',
        },
      },
      {
        onError: handleError,
      },
    );

    // ModalWindows.open('Cart', {
    //   products: [
    //     {
    //       ...compiledProduct,
    //       isModular: false,
    //       paymentTypeId: -1,
    //     },
    //   ],
    //   analyticsLabel: 'Избранное',
    // });
  }, [hasInCart, addToCart, compiledProduct, product, handleError, openPage, region.url]);

  return hasInCart ? (
    <Button
      {...restProps}
      wide
      className={cn(styles.buyButton, { [styles.hasInCart]: hasInCart }, className)}
      theme='transparent'
      data-testid='sidebar-buy-button'
      onClick={handleClick}
    >
      <div className={styles.iconCheck} />
      {t('ui.in-cart')}
    </Button>
  ) : (
    <Button
      {...restProps}
      wide
      theme='transparent'
      className={cn(styles.buyButton, className)}
      data-testid='sidebar-buy-button'
      waiting={addToCart.isPending}
      onClick={handleClick}
    >
      {t('ui.add-to-cart')}
    </Button>
  );
};

export default memo(ConstructorBuyButton);
